import React, { useMemo } from 'react';
import { useQuery, gql, TypedDocumentNode } from '@apollo/client';
import { Stack } from '@moda/om';
import { compact, titleize } from '@moda/portal-stanchions';
import { RECENCY } from '../../constants';
import { VariantCellFragment } from '../../generated/types';
import { useModuleTracking } from '../../hooks/useModuleTracking';
import { usePLPParams } from '../../hooks/usePLPParams';
import { DividerModule, DIVIDER_LAYOUT_TYPES } from '../DividerPumoModule';
import { VariantCell, VARIANT_CELL_FRAGMENT } from '../Grid/GridCell/VariantCell';
import { CAROUSEL_DEFAULT_CONFIG, SwiperCarousel } from '../Swiper';

const EMPTY_STATE_CAROUSEL_PATH = 'women/clothing';
const EMPTY_STATE_CAROUSEL_FETCH_SIZE = 100;
const EMPTY_STATE_CAROUSEL_SIZE = 20;

type ProductListingEmptyStateSubcategoryCarouselQuery = {
  productListing: {
    __typename: 'CollectionResult';
    variants: {
      __typename: 'VariantConnection';
      edges: {
        __typename: 'VariantEdge';
        node: VariantCellFragment | null;
      }[];
    };
  } | null;
};

type ProductListingEmptyStateSubcategoryCarouselQueryVariables = {
  after: string;
  categoryPath?: string;
  countryCode?: string;
  dynamicReRanking?: boolean;
  scoring?: string;
  slug: string;
};

const EMPTY_STATE_SUBCATEGORY_CAROUSEL_QUERY: TypedDocumentNode<
  ProductListingEmptyStateSubcategoryCarouselQuery,
  ProductListingEmptyStateSubcategoryCarouselQueryVariables
> = gql`
  query ProductListingEmptyStateSubcategoryCarouselQuery(
    $after: String!
    $categoryPath: String
    $countryCode: String
    $dynamicReRanking: Boolean
    $scoring: String
    $slug: String!
  ) {
    productListing: product_listing(
      section: "shop"
      slug: $slug
      input: {
        category_path: $categoryPath
        country_code: $countryCode
        dynamic_reranking: $dynamicReRanking
        scoring: $scoring
      }
    ) {
      variants(after: $after) {
        edges {
          node {
            ...VariantCellFragment
          }
        }
      }
    }
  }
  ${VARIANT_CELL_FRAGMENT}
`;

type EmptyStateCarouselSection = {
  identifier: string;
  title: string;
  variants: VariantCellFragment[];
};

const getCarouselIdentifier = (identifier: string) =>
  `ProductListingEmptyStateSubcategoryCarousel-${identifier}`.replace(/[^a-zA-Z0-9-_]/g, '-');

const ProductListingEmptyStateCarouselSection: React.FC<EmptyStateCarouselSection> = ({
  identifier,
  title,
  variants
}) => {
  const { ref, onClick } = useModuleTracking({
    name: title,
    attachClickHandler: false,
    variants,
    viewThreshold: 0.75,
    trackingPage: 'Empty search result'
  });

  return (
    <div ref={ref}>
      <Stack space={3}>
        <DividerModule layoutType={DIVIDER_LAYOUT_TYPES.LINE_ABOVE} text={title} />

        <SwiperCarousel
          identifier={identifier}
          classNameWrapper="ProductListingEmptyStateSubcategoryCarousel__carousel"
          {...CAROUSEL_DEFAULT_CONFIG}
          pagination={{ enabled: false }}
          circleArrow
          onClick={(swiper, event) => onClick(event)}
        >
          {variants.map(variant => (
            <VariantCell key={variant.id} variant={variant} />
          ))}
        </SwiperCarousel>
      </Stack>
    </div>
  );
};

export const ProductListingEmptyStateSubcategoryCarousel: React.FC = () => {
  const {
    params: { q },
    queryVariables
  } = usePLPParams();

  const after = useMemo(
    () =>
      btoa(
        JSON.stringify({
          page: 0,
          first: EMPTY_STATE_CAROUSEL_FETCH_SIZE,
          sort: RECENCY.value
        })
      ),
    []
  );

  const { data, loading, error } = useQuery(EMPTY_STATE_SUBCATEGORY_CAROUSEL_QUERY, {
    variables: {
      after,
      categoryPath: EMPTY_STATE_CAROUSEL_PATH,
      countryCode: queryVariables.countryCode,
      dynamicReRanking: queryVariables.dynamicReRanking,
      scoring: queryVariables.scoring,
      slug: EMPTY_STATE_CAROUSEL_PATH
    },
    skip: !q
  });

  const variants = useMemo(
    () => compact(data?.productListing?.variants.edges.map(edge => edge?.node) || []),
    [data]
  );

  const carouselSections = useMemo<EmptyStateCarouselSection[]>(() => {
    const dresses = variants
      .filter(
        variant => variant.category.includes('CLOTHING') && variant.subcategory.includes('DRESSES')
      )
      .slice(0, EMPTY_STATE_CAROUSEL_SIZE);

    return [
      {
        identifier: getCarouselIdentifier('whats-new-women-clothing'),
        title: titleize("What's New"),
        variants: variants.slice(0, EMPTY_STATE_CAROUSEL_SIZE)
      },
      {
        identifier: getCarouselIdentifier('dresses-women-clothing'),
        title: titleize('Dresses'),
        variants: dresses
      }
    ].filter(section => section.variants.length > 0);
  }, [variants]);

  if (!q || loading || error || carouselSections.length === 0) return null;

  return (
    <Stack space={3}>
      {carouselSections.map(section => (
        <ProductListingEmptyStateCarouselSection key={section.identifier} {...section} />
      ))}
    </Stack>
  );
};
